import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { Modal, Box, Button, SpaceBetween, Spinner, StatusIndicator, SegmentedControl, FormField, Textarea, Tabs } from "@cloudscape-design/components";
import { useAuth } from "../../context/AuthContext";
import { useChatSession } from "../../context/ChatSessionContext";
import { useNotification } from "../../context/NotificationContext";
import { boxerService } from "../../services/boxerService";

export default function AppTopNavigation() {
  const { user, logout, isAdmin } = useAuth();
  const { clearAllSessions, clearAllPrompts } = useChatSession();
  const { addNotification } = useNotification();
  const history = useHistory();

  // Settings modal
  const [settingsOpen, setSettingsOpen]           = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState("data");

  // Confirmation modals
  const [confirmChats, setConfirmChats]           = useState(false);
  const [confirmPrompts, setConfirmPrompts]       = useState(false);
  const [confirmMemory, setConfirmMemory]         = useState(false);

  // Loading states for each clear action
  const [clearingChats, setClearingChats]         = useState(false);
  const [clearingPrompts, setClearingPrompts]     = useState(false);
  const [clearingMemory, setClearingMemory]       = useState(false);

  // Memory detail modal
  const [memoryModalOpen, setMemoryModalOpen]     = useState(false);
  const [memoryData, setMemoryData]               = useState(null);
  const [memoryLoading, setMemoryLoading]         = useState(false);

  // Preferences tab state
  const DEFAULT_PREFS = {
    opslense:  { response_length: "default", response_format: "default" },
    docusense: { response_length: "default", response_format: "default" },
    focus: "",
  };
  const [prefs, setPrefs]               = useState(DEFAULT_PREFS);
  const [prefsLoaded, setPrefsLoaded]   = useState(false);
  const [prefsSaving, setPrefsSaving]   = useState(false);

  const handleClearChats = async () => {
    setClearingChats(true);
    try { await clearAllSessions(); } catch {}
    setClearingChats(false);
    setConfirmChats(false);
  };

  const handleClearPrompts = async () => {
    setClearingPrompts(true);
    try { await clearAllPrompts(); } catch {}
    setClearingPrompts(false);
    setConfirmPrompts(false);
  };

  const handleClearMemory = async () => {
    setClearingMemory(true);
    try {
      await boxerService.clearMemory();
      addNotification("success", "Memory cleared");
      setMemoryData({ facts: [], session_count: 0, last_updated: null });
    } catch {}
    setClearingMemory(false);
    setConfirmMemory(false);
  };

  const loadMemory = async () => {
    setMemoryLoading(true);
    try {
      const data = await boxerService.getMyMemory();
      setMemoryData(data);
    } catch {
      setMemoryData({ facts: [], session_count: 0, last_updated: null });
    } finally {
      setMemoryLoading(false);
    }
  };

  const openMemoryModal = () => {
    setMemoryModalOpen(true);
    loadMemory();
  };

  const loadPreferences = async () => {
    try {
      const data = await boxerService.getPreferences();
      setPrefs({
        opslense:  data.opslense  || DEFAULT_PREFS.opslense,
        docusense: data.docusense || DEFAULT_PREFS.docusense,
        focus:     data.focus     || "",
      });
    } catch {
      setPrefs(DEFAULT_PREFS);
    } finally {
      setPrefsLoaded(true);
    }
  };

  const savePreferences = async () => {
    setPrefsSaving(true);
    try {
      await boxerService.savePreferences(prefs.opslense, prefs.docusense, prefs.focus);
      addNotification("success", "Preferences saved");
    } catch {
      addNotification("error", "Failed to save preferences");
    } finally {
      setPrefsSaving(false);
    }
  };

  const setModePrefs = (mode, key, value) => {
    setPrefs(p => ({ ...p, [mode]: { ...p[mode], [key]: value } }));
  };

  const userItems = [];
  if (user?.email) userItems.push({ id: "email", text: user.email, disabled: true });
  userItems.push({ id: "div1", itemType: "divider" });
  if (isAdmin()) userItems.push({ id: "admin", text: "Admin Portal", iconName: "settings" });
  userItems.push({ id: "logout", text: "Sign Out", iconName: "key" });

  return (
    <>
      <TopNavigation
        identity={{
          href: "/",
          title: "SmartOps",
          logo: { src: "/Assets/images/iWMSLogo_white.png", alt: "iWMS" },
          onFollow: (e) => { e.preventDefault(); history.push("/"); },
        }}
        utilities={[
          {
            type:      "button",
            iconName:  "settings",
            title:     "Settings",
            onClick:   () => { setSettingsOpen(true); if (!prefsLoaded) loadPreferences(); },
          },
          {
            type:        "menu-dropdown",
            text:        user ? `${user.username}` : "Account",
            iconName:    "user-profile",
            onItemClick: ({ detail }) => {
              if (detail.id === "logout") { logout(); history.push("/login"); }
              if (detail.id === "admin")  history.push("/admin");
            },
            items: userItems,
          },
        ]}
      />

      {/* ── Settings modal ── */}
      <Modal
        visible={settingsOpen}
        onDismiss={() => setSettingsOpen(false)}
        header="Settings"
        size="medium"
      >
        <Tabs
          activeTabId={activeSettingsTab}
          onChange={({ detail }) => setActiveSettingsTab(detail.activeTabId)}
          tabs={[
            {
              id: "data",
              label: "Chats & Prompts",
              content: (
                <SpaceBetween size="s">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #e3e8ef" }}>
                    <div>
                      <Box variant="p" fontWeight="bold">Chats</Box>
                      <Box variant="p" color="text-body-secondary" fontSize="body-s">
                        Permanently delete all your chat history
                      </Box>
                    </div>
                    <Button variant="normal" onClick={() => setConfirmChats(true)}>Clear All</Button>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
                    <div>
                      <Box variant="p" fontWeight="bold">Saved Prompts</Box>
                      <Box variant="p" color="text-body-secondary" fontSize="body-s">
                        Permanently delete all your saved prompt templates
                      </Box>
                    </div>
                    <Button variant="normal" onClick={() => setConfirmPrompts(true)}>Clear All</Button>
                  </div>
                </SpaceBetween>
              ),
            },
            {
              id: "memory",
              label: "Memory",
              content: (
                <SpaceBetween size="s">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
                    <div>
                      <Box variant="p" fontWeight="bold">My Memory</Box>
                      <Box variant="p" color="text-body-secondary" fontSize="body-s">
                        Facts the AI has learned about you from DocuSense conversations
                      </Box>
                    </div>
                    <Button variant="normal" iconName="search" onClick={openMemoryModal}>
                      Show Memory
                    </Button>
                  </div>
                </SpaceBetween>
              ),
            },
            {
              id: "preferences",
              label: "Preferences",
              content: (
                <SpaceBetween size="l">
                  <div style={{ borderBottom: "1px solid #e3e8ef", paddingBottom: 16 }}>
                    <Box variant="h4" padding={{ bottom: "s" }}>OpsLense</Box>
                    <SpaceBetween size="s">
                      <FormField label="Response Length">
                        <SegmentedControl
                          selectedId={prefs.opslense.response_length}
                          onChange={({ detail }) => setModePrefs("opslense", "response_length", detail.selectedId)}
                          options={[
                            { id: "default",  text: "Default"  },
                            { id: "brief",    text: "Brief"    },
                            { id: "balanced", text: "Balanced" },
                            { id: "detailed", text: "Detailed" },
                          ]}
                        />
                      </FormField>
                      <FormField label="Response Format">
                        <SegmentedControl
                          selectedId={prefs.opslense.response_format}
                          onChange={({ detail }) => setModePrefs("opslense", "response_format", detail.selectedId)}
                          options={[
                            { id: "default",    text: "Default"    },
                            { id: "bullets",    text: "Bullets"    },
                            { id: "paragraphs", text: "Paragraphs" },
                          ]}
                        />
                      </FormField>
                    </SpaceBetween>
                  </div>
                  <div style={{ borderBottom: "1px solid #e3e8ef", paddingBottom: 16 }}>
                    <Box variant="h4" padding={{ bottom: "s" }}>DocuSense</Box>
                    <SpaceBetween size="s">
                      <FormField label="Response Length">
                        <SegmentedControl
                          selectedId={prefs.docusense.response_length}
                          onChange={({ detail }) => setModePrefs("docusense", "response_length", detail.selectedId)}
                          options={[
                            { id: "default",  text: "Default"  },
                            { id: "brief",    text: "Brief"    },
                            { id: "balanced", text: "Balanced" },
                            { id: "detailed", text: "Detailed" },
                          ]}
                        />
                      </FormField>
                      <FormField label="Response Format">
                        <SegmentedControl
                          selectedId={prefs.docusense.response_format}
                          onChange={({ detail }) => setModePrefs("docusense", "response_format", detail.selectedId)}
                          options={[
                            { id: "default",    text: "Default"    },
                            { id: "bullets",    text: "Bullets"    },
                            { id: "paragraphs", text: "Paragraphs" },
                          ]}
                        />
                      </FormField>
                    </SpaceBetween>
                  </div>
                  <FormField
                    label="My Focus"
                    description="Optional — scopes every AI answer to your role. e.g. I manage DC Joburg"
                  >
                    <Textarea
                      value={prefs.focus}
                      onChange={({ detail }) => setPrefs(p => ({ ...p, focus: detail.value }))}
                      placeholder="e.g. I manage DC Joburg and focus on at-risk shipments"
                      rows={2}
                    />
                  </FormField>
                  <Box float="right">
                    <Button variant="primary" loading={prefsSaving} onClick={savePreferences}>
                      Save Preferences
                    </Button>
                  </Box>
                </SpaceBetween>
              ),
            },
          ]}
        />
      </Modal>

      {/* ── Confirm: clear all chats ── */}
      <Modal
        visible={confirmChats}
        onDismiss={() => setConfirmChats(false)}
        header="Clear all chats?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmChats(false)}>Cancel</Button>
              <Button variant="primary" loading={clearingChats} onClick={handleClearChats}>Clear</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>All your chat history will be permanently deleted. This cannot be undone.</Box>
      </Modal>

      {/* ── Confirm: clear all prompts ── */}
      <Modal
        visible={confirmPrompts}
        onDismiss={() => setConfirmPrompts(false)}
        header="Clear all saved prompts?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmPrompts(false)}>Cancel</Button>
              <Button variant="primary" loading={clearingPrompts} onClick={handleClearPrompts}>Clear</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>All your saved prompt templates will be permanently deleted. This cannot be undone.</Box>
      </Modal>

      {/* ── Memory detail modal ── */}
      <Modal
        visible={memoryModalOpen}
        onDismiss={() => setMemoryModalOpen(false)}
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            My DocuSense Memory
            <Button
              variant="icon"
              iconName="refresh"
              ariaLabel="Refresh memory"
              loading={memoryLoading}
              onClick={loadMemory}
            />
          </div>
        }
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setMemoryModalOpen(false)}>Close</Button>
              <Button
                variant="primary"
                disabled={!memoryData || (memoryData.facts || []).length === 0}
                onClick={() => { setMemoryModalOpen(false); setConfirmMemory(true); }}
              >
                Clear Memory
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {memoryLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Spinner size="normal" />
          </div>
        ) : memoryData ? (
          <SpaceBetween size="m">
            <Box color="text-body-secondary" fontSize="body-s">
              {memoryData.session_count || 0} DocuSense {(memoryData.session_count || 0) === 1 ? "session" : "sessions"} contributed
              {memoryData.last_updated
                ? ` · Last updated: ${new Date(memoryData.last_updated).toLocaleString()}`
                : " · Never updated"}
            </Box>
            {(memoryData.facts || []).length === 0 ? (
              <StatusIndicator type="info">
                No memory yet. Memory builds up after DocuSense conversations.
              </StatusIndicator>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {memoryData.facts.map((f, i) => (
                  <li key={i} style={{ marginBottom: 6, fontSize: 14 }}>
                    {f.fact}
                    <span style={{ color: "#687078", fontSize: 12, marginLeft: 6 }}>
                      ({f.mentions}x)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SpaceBetween>
        ) : null}
      </Modal>

      {/* ── Confirm: clear memory ── */}
      <Modal
        visible={confirmMemory}
        onDismiss={() => setConfirmMemory(false)}
        header="Clear your memory?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmMemory(false)}>Cancel</Button>
              <Button variant="primary" loading={clearingMemory} onClick={handleClearMemory}>Clear</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>
          This will erase everything the AI has learned about you from DocuSense conversations.
          Your chat history will not be deleted. This cannot be undone.
        </Box>
      </Modal>
    </>
  );
}
